import { AdFarmApiClient, AdFarmApiError } from "./AdFarmApiClient";
import { AuthRepository } from "./AuthRepository";
import { AuthResult, MobileOverview, MobileRole } from "./AdFarmTypes";
import { PayoutRepository } from "./PayoutRepository";
import { RevenueRepository } from "./RevenueRepository";
import { RewardSessionService } from "./RewardSessionService";
import { RiskService } from "./RiskService";
import { SecureStorage } from "./SecureStorage";

export class AdFarmSession extends Laya.EventDispatcher {
    static readonly AUTH_CHANGED = "adfarm-auth-changed";
    static readonly OVERVIEW_CHANGED = "adfarm-overview-changed";
    private static readonly TOKEN_KEY = "mobile-token";
    private static readonly ROLE_KEY = "mobile-role";
    private static _instance: AdFarmSession;

    static get instance(): AdFarmSession {
        if (!this._instance) this._instance = new AdFarmSession();
        return this._instance;
    }

    readonly api = new AdFarmApiClient();
    readonly auth = new AuthRepository(this.api);
    readonly risk = new RiskService(this.api);
    readonly revenue = new RevenueRepository(this.api);
    readonly payout = new PayoutRepository(this.api);
    readonly rewards = new RewardSessionService(this.api, this.risk);
    token = "";
    role: MobileRole = "user";
    overview: MobileOverview | null = null;

    private constructor() {
        super();
        this.api.onUnauthorized = () => this.clearAuth();
    }

    get authenticated(): boolean {
        return !!this.token;
    }

    async restore(): Promise<void> {
        await this.risk.restoreConsent();
        this.token = await SecureStorage.get(AdFarmSession.TOKEN_KEY);
        const role = await SecureStorage.get(AdFarmSession.ROLE_KEY);
        this.role = role === "secondaryAgent" ? "secondaryAgent" : "user";
        this.api.setToken(this.token);
        if (!this.token) return;
        try {
            await this.refreshOverview();
            this.startOverviewRefresh();
        } catch (error) {
            if (error instanceof AdFarmApiError && error.status === 401) await this.clearAuth();
        }
    }

    async login(input: { phone: string; code: string; role: MobileRole }): Promise<void> {
        const result = await this.auth.login({ ...input, risk: await this.risk.getPayload() });
        await this.acceptAuth(result, input.role);
    }

    async register(input: { phone: string; code: string; inviteCode: string; role: MobileRole; inviteEntryLockedToUser: boolean }): Promise<void> {
        const result = await this.auth.register({ ...input, risk: await this.risk.getPayload() });
        await this.acceptAuth(result, input.role);
    }

    async sendCode(phone: string): Promise<unknown> {
        return this.auth.sendCode(phone, await this.risk.getPayload());
    }

    async refreshOverview(): Promise<MobileOverview> {
        const overview = await this.revenue.overview();
        this.overview = overview;
        this.event(AdFarmSession.OVERVIEW_CHANGED, overview);
        return overview;
    }

    async claimQuizReward(level: number): Promise<{ reward: number; totalBalance: number; alreadyClaimed: boolean }> {
        const result = await this.api.claimQuizReward<{ reward: number; totalBalance: number; alreadyClaimed: boolean }>(level);
        await this.refreshOverview();
        return result;
    }

    async logout(): Promise<void> {
        await this.clearAuth();
    }

    private async acceptAuth(result: AuthResult, fallbackRole: MobileRole): Promise<void> {
        if (!result.token) throw new Error("服务端未返回登录凭证");
        this.token = result.token;
        this.role = result.role || fallbackRole;
        this.api.setToken(this.token);
        await SecureStorage.set(AdFarmSession.TOKEN_KEY, this.token);
        await SecureStorage.set(AdFarmSession.ROLE_KEY, this.role);
        await this.refreshOverview();
        this.startOverviewRefresh();
        this.event(AdFarmSession.AUTH_CHANGED, true);
    }

    private startOverviewRefresh(): void {
        this.revenue.startForegroundRefresh(
            overview => {
                this.overview = overview;
                this.event(AdFarmSession.OVERVIEW_CHANGED, overview);
            },
        );
    }

    private async clearAuth(): Promise<void> {
        this.token = "";
        this.overview = null;
        this.revenue.stopForegroundRefresh();
        this.api.clearToken();
        await SecureStorage.remove(AdFarmSession.TOKEN_KEY);
        await SecureStorage.remove(AdFarmSession.ROLE_KEY);
        this.event(AdFarmSession.AUTH_CHANGED, false);
    }
}
